import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useRouter } from 'next/router';

import { useMedia } from '@tager/web-core';

import DetailedCarPlaceholder from '@/modules/DetailedCar/DetailedCarPlaceholder';
import {
  getCarBlogThunk,
  getDetailedCarThunk,
  getSimilarCarsThunk,
  selectDetailedCarData,
  selectDetailedCarStatus,
} from '@/store/reducers/detailed-car';
import { useTypedDispatch, useTypedSelector } from '@/store/store';
import { fetchCarDetailedBannerThunk } from '@/store/reducers/banners';
import { getSimilarCarsDataParams } from '@/modules/DetailedCar/DetailedCar.helper';
import { fetchSeoTemplateThunk } from '@/store/reducers/seo-template';
import { media } from '@/utils/mixin';
import { googleEvent } from '@/utils/events';
import useSettingItem from '@/hooks/useSettingItem';
import { parseStockCarIdFromSlug } from '@/utils/stockCarId';

import SidePanelNew from './components/SidePanelNew';
import DetailedCarNewMain from './components/DetailedCarNewMain';
import CompareButton from './components/CompareButton';

function DetailedCarNew() {
  const dispatch = useTypedDispatch();
  const router = useRouter();
  const status = useTypedSelector(selectDetailedCarStatus);
  const isTablet = useMedia('(max-width: 1259px)');
  const data = useTypedSelector(selectDetailedCarData);
  const historyChangeGoogleEvent = useSettingItem(
    'DETAILED_CAR_NEW_HISTORY_CHANGE_GOOGLE_EVENT'
  );

  useEffect(() => {
    if (status === 'IDLE') {
      (async () => {
        try {
          const carId = router.query?.slug?.[0];
          const parsedCarId = parseStockCarIdFromSlug(
            typeof carId === 'string' ? carId : undefined
          );

          if (parsedCarId === null) {
            return;
          }

          const detailedCarData = await dispatch(
            getDetailedCarThunk(parsedCarId)
          );
          await Promise.all([
            dispatch(
              getSimilarCarsThunk(
                'new',
                String(parsedCarId),
                getSimilarCarsDataParams(detailedCarData)
              )
            ),
            dispatch(
              fetchCarDetailedBannerThunk({
                type: 'new',
                brand: String(detailedCarData.catalog.brand.id),
                model: String(detailedCarData.catalog.model?.id),
              })
            ),
            dispatch(getCarBlogThunk('new', String(parsedCarId))),
            dispatch(fetchSeoTemplateThunk('stock_new_detailed')),
          ]);
        } catch (error) {
          console.error(error);
        }
      })();
    }
  }, [router.query]);

  useEffect(() => {
    if (historyChangeGoogleEvent && data) {
      googleEvent(historyChangeGoogleEvent, {
        h1_brand: data.catalog.brand.name,
        h1_model: data.catalog.model?.name,
        year_manufactured: data.params.year?.toString(),
      });
    }
  }, [data]);

  return (
    <Component>
      <ContentContainer>
        {status !== 'SUCCESS' ? (
          <DetailedCarPlaceholder />
        ) : (
          <React.Fragment>
            <Container>
              <DetailedCarNewMain />
              {isTablet || <SidePanelNew />}
            </Container>

            <CompareButton />
          </React.Fragment>
        )}
      </ContentContainer>
    </Component>
  );
}

export default DetailedCarNew;

const Component = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 25px 0 60px;
  background: ${(props) => props.theme.white};

  ${media.tablet(css`
    padding: 12px 0 60px;
  `)}
`;

const ContentContainer = styled.div`
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 1650px) {
    max-width: 1400px;
  }

  @media (max-width: 1550px) {
    max-width: 1300px;
  }

  @media (max-width: 1450px) {
    max-width: 1200px;
  }

  ${media.tablet(css`
    max-width: none;
  `)}
`;

const Container = styled.div`
  display: flex;
  justify-content: space-between;
`;
